import { css } from "@emotion/react";

import type { Source, UpsellPopoverState } from "./utils";
import { getSourceLabel, getSourceUrl, isBookSource } from "./utils";
import { Link } from "react-router";

type Props = {
  content: string;
  sources: Source[];
  publicationsBySlug?: Record<string, unknown>;
  setUpsellPopover: (p: UpsellPopoverState | null) => void;
};

// Match [Source N], [Source N, M], and bare [N], [N, M] patterns.
// Bare numbers are capped at 1-99 to avoid matching year brackets
// in case citations like [2006] UKHL 24.
const CITATION_PATTERN =
  /(\[Source\s+\d+(?:\s*,\s*(?:Source\s+)?\d+)*\])|(\[(?:[1-9]\d?)(?:\s*,\s*(?:[1-9]\d?))*\])|(https?:\/\/[^\s)]+)/g;

export const ContentRenderer = ({
  content,
  sources,
  publicationsBySlug,
  setUpsellPopover,
}: Props) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const pattern = new RegExp(CITATION_PATTERN.source, "g");

  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    const sourceRef = match[1] || match[2];
    if (sourceRef) {
      // [Source N], [Source N, M], [N], or [N, M]
      const refs = sourceRef.match(/\d+/g) ?? [];
      refs.forEach((numStr, i) => {
        const idx = parseInt(numStr, 10) - 1; // 1-indexed in LLM output
        const src = sources[idx];
        if (src) {
          const { url, target, rel } = getSourceUrl(src, publicationsBySlug);
          const hasUpsell = !url && isBookSource(src) && !!src.purchase_url;
          parts.push(
            <Link
              key={`ref-${match!.index}-${i}`}
              to={url ?? "#"}
              target={target}
              rel={rel}
              onClick={(e) => {
                if (url) return; // let the link navigate
                e.preventDefault();
                if (hasUpsell) {
                  setUpsellPopover({
                    anchorEl: e.currentTarget as HTMLElement,
                    bookTitle: getSourceLabel(src),
                    purchaseUrl: src.purchase_url!,
                  });
                }
              }}
              css={({ colours }) => css`
                color: ${colours.primary.mid.dark20};
                font-size: 11px;
                font-weight: 600;
                text-decoration: none;
                background: ${colours.primary.light._30};
                border-radius: 4px;
                padding: 0 4px;
                cursor: ${url || hasUpsell ? "pointer" : "default"};

                &:hover {
                  background: ${colours.primary.light._10};
                }
              `}
            >
              {numStr}
            </Link>,
          );
          if (i < refs.length - 1) parts.push(" ");
        } else {
          // Unknown source index — render as plain text
          parts.push(`[Source ${numStr}]`);
        }
      });
    } else if (match[3]) {
      // Bare URL
      parts.push(
        <a
          key={`url-${match.index}`}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          css={({ colours }) => css`
            color: ${colours.primary.mid.dark20};
            text-decoration: underline;
          `}
        >
          {match[3]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text (may include partial "[Source" at stream end)
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return (
    <div
      css={({ colours, customTypography }) => css`
        ${customTypography.paragraph._2}
        color: ${colours.primary.dark._20};
        white-space: pre-wrap;
        line-height: 1.6;
      `}
    >
      {parts}
    </div>
  );
};
